/**
 * Program Store
 * =============
 * State management for programs and program builder.
 */

import { create } from 'zustand'
import {
  programService,
  type ProgramWithItems,
  type ProgramFilters,
  type ProgramItemWithVideo,
} from '../services'
import type { Program, VideoVisibility } from '../types/database.types'

interface ProgramState {
  // Data
  programs: Program[]
  templates: Program[]
  selectedProgram: ProgramWithItems | null
  isLoading: boolean
  error: string | null

  // Filters
  filters: ProgramFilters
  searchQuery: string

  // Builder state
  builderItems: ProgramItemWithVideo[]
  isDragging: boolean
  dragItemId: string | null

  // Clipboard for task duplication
  clipboardTask: ProgramItemWithVideo | null

  // Actions
  fetchPrograms: (userId: string) => Promise<void>
  fetchTemplates: (userId: string) => Promise<void>
  fetchProgramById: (programId: string) => Promise<ProgramWithItems | null>
  createProgram: (program: Parameters<typeof programService.createProgram>[0]) => Promise<Program>
  updateProgram: (programId: string, updates: Parameters<typeof programService.updateProgram>[1]) => Promise<Program | null>
  deleteProgram: (programId: string) => Promise<boolean>
  duplicateProgram: (programId: string, userId: string, newTitle?: string) => Promise<ProgramWithItems | null>
  toggleTemplate: (programId: string, isTemplate: boolean) => Promise<Program | null>
  setVisibility: (programId: string, visibility: VideoVisibility) => Promise<Program | null>
  setSelectedProgram: (program: ProgramWithItems | null) => void
  setFilters: (filters: Partial<ProgramFilters>) => void
  setSearchQuery: (query: string) => void
  clearFilters: () => void

  // Builder actions
  setBuilderItems: (items: ProgramItemWithVideo[]) => void
  addVideoToBuilder: (programId: string, videoId: string) => Promise<void>
  removeVideoFromBuilder: (programItemId: string) => Promise<void>
  reorderBuilderItems: (programId: string, itemIds: string[]) => Promise<void>
  updateBuilderItem: (programItemId: string, updates: Parameters<typeof programService.updateProgramItem>[1]) => Promise<void>
  setDragging: (isDragging: boolean, itemId?: string | null) => void

  // Task clipboard actions
  copyTaskToClipboard: (task: ProgramItemWithVideo) => void
  pasteTaskToProgram: (targetProgramId: string) => Promise<void>
  clearClipboard: () => void
}

export const useProgramStore = create<ProgramState>((set, get) => ({
  // Initial state
  programs: [],
  templates: [],
  selectedProgram: null,
  isLoading: false,
  error: null,
  filters: {},
  searchQuery: '',
  builderItems: [],
  isDragging: false,
  dragItemId: null,
  clipboardTask: null,

  // Fetch all programs
  fetchPrograms: async (userId: string) => {
    set({ isLoading: true, error: null })
    try {
      const { filters, searchQuery } = get()
      const programs = await programService.getPrograms(userId, {
        ...filters,
        search: searchQuery || undefined,
      })
      set({ programs, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch programs',
        isLoading: false,
      })
    }
  },

  // Fetch a single program with items
  fetchProgramById: async (programId: string) => {
    set({ isLoading: true, error: null })
    try {
      const program = await programService.getProgramById(programId)
      set({
        selectedProgram: program,
        builderItems: program?.items || [],
        isLoading: false,
      })
      return program
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch program',
        isLoading: false,
      })
      return null
    }
  },

  // Create a new program
  createProgram: async (programData) => {
    set({ isLoading: true, error: null })
    try {
      const program = await programService.createProgram(programData)
      set((state) => ({
        programs: [program, ...state.programs],
        isLoading: false,
      }))
      return program
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create program',
        isLoading: false,
      })
      throw error
    }
  },

  // Update a program
  updateProgram: async (programId, updates) => {
    set({ isLoading: true, error: null })
    try {
      const updated = await programService.updateProgram(programId, updates)
      if (updated) {
        set((state) => ({
          programs: state.programs.map((p) =>
            p.id === programId ? { ...p, ...updated } : p
          ),
          selectedProgram:
            state.selectedProgram?.id === programId
              ? { ...state.selectedProgram, ...updated }
              : state.selectedProgram,
          isLoading: false,
        }))
      }
      return updated
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update program',
        isLoading: false,
      })
      throw error
    }
  },

  // Delete a program
  deleteProgram: async (programId) => {
    set({ isLoading: true, error: null })
    try {
      await programService.deleteProgram(programId)
      set((state) => ({
        programs: state.programs.filter((p) => p.id !== programId),
        selectedProgram:
          state.selectedProgram?.id === programId ? null : state.selectedProgram,
        isLoading: false,
      }))
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete program',
        isLoading: false,
      })
      return false
    }
  },

  // Duplicate a program
  duplicateProgram: async (programId, userId, newTitle) => {
    set({ isLoading: true, error: null })
    try {
      const newProgram = await programService.duplicateProgram(
        programId,
        userId,
        newTitle
      )
      if (newProgram) {
        set((state) => ({
          programs: [newProgram, ...state.programs],
          isLoading: false,
        }))
      }
      return newProgram
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to duplicate program',
        isLoading: false,
      })
      return null
    }
  },

  // Toggle template status
  toggleTemplate: async (programId, isTemplate) => {
    set({ isLoading: true, error: null })
    try {
      const updated = await programService.toggleTemplate(programId, isTemplate)
      if (updated) {
        set((state) => ({
          programs: state.programs.map((p) =>
            p.id === programId ? { ...p, is_template: isTemplate } : p
          ),
          templates: isTemplate
            ? [...state.templates, updated]
            : state.templates.filter((t) => t.id !== programId),
          selectedProgram:
            state.selectedProgram?.id === programId
              ? { ...state.selectedProgram, is_template: isTemplate }
              : state.selectedProgram,
          isLoading: false,
        }))
      }
      return updated
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to toggle template',
        isLoading: false,
      })
      return null
    }
  },

  // Set program visibility
  setVisibility: async (programId, visibility) => {
    try {
      const updated = await programService.setVisibility(programId, visibility)
      if (updated) {
        set((state) => ({
          programs: state.programs.map((p) =>
            p.id === programId ? { ...p, visibility } : p
          ),
          selectedProgram:
            state.selectedProgram?.id === programId
              ? { ...state.selectedProgram, visibility }
              : state.selectedProgram,
        }))
      }
      return updated
    } catch (error) {
      console.error('Failed to set visibility:', error)
      throw error
    }
  },

  // Fetch user's templates
  fetchTemplates: async (userId) => {
    try {
      const templates = await programService.getTemplates(userId)
      set({ templates })
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    }
  },

  // Set selected program
  setSelectedProgram: (program) => {
    set({
      selectedProgram: program,
      builderItems: program?.items || [],
    })
  },

  // Set filters
  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }))
  },

  // Set search query
  setSearchQuery: (query) => {
    set({ searchQuery: query })
  },

  // Clear all filters
  clearFilters: () => {
    set({ filters: {}, searchQuery: '' })
  },

  // Set builder items directly
  setBuilderItems: (items) => {
    set({ builderItems: items })
  },

  // Add video to program builder
  addVideoToBuilder: async (programId, videoId) => {
    set({ isLoading: true, error: null })
    try {
      await programService.addVideoToProgram(programId, videoId)
      // Refetch to get full item with video details
      const program = await programService.getProgramById(programId)
      set({
        builderItems: program?.items || [],
        selectedProgram: program,
        isLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add video',
        isLoading: false,
      })
      throw error
    }
  },

  // Remove video from program builder
  removeVideoFromBuilder: async (programItemId) => {
    set({ isLoading: true, error: null })
    try {
      await programService.removeVideoFromProgram(programItemId)
      set((state) => ({
        builderItems: state.builderItems.filter((i) => i.id !== programItemId),
        isLoading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove video',
        isLoading: false,
      })
      throw error
    }
  },

  // Reorder builder items
  reorderBuilderItems: async (programId, itemIds) => {
    // Optimistic update
    const { builderItems } = get()
    const reorderedItems = itemIds
      .map((id) => builderItems.find((i) => i.id === id))
      .filter((i): i is ProgramItemWithVideo => i !== undefined)
      .map((item, index) => ({ ...item, position: index }))

    set({ builderItems: reorderedItems })

    try {
      await programService.reorderProgramItems(programId, itemIds)
    } catch (error) {
      // Revert on error
      set({ builderItems })
      throw error
    }
  },

  // Update a builder item
  updateBuilderItem: async (programItemId, updates) => {
    set({ isLoading: true, error: null })
    try {
      await programService.updateProgramItem(programItemId, updates)
      set((state) => ({
        builderItems: state.builderItems.map((i) =>
          i.id === programItemId ? { ...i, ...updates } : i
        ),
        isLoading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update item',
        isLoading: false,
      })
      throw error
    }
  },

  // Set dragging state
  setDragging: (isDragging, itemId = null) => {
    set({ isDragging, dragItemId: itemId })
  },

  // Copy task to clipboard
  copyTaskToClipboard: (task) => {
    set({ clipboardTask: task })
  },

  // Paste task from clipboard to program
  pasteTaskToProgram: async (targetProgramId) => {
    const { clipboardTask } = get()
    if (!clipboardTask) return

    set({ isLoading: true, error: null })
    try {
      await programService.duplicateTaskToProgram(clipboardTask.id, targetProgramId)
      // Refetch the program to get updated items
      const program = await programService.getProgramById(targetProgramId)
      set({
        builderItems: program?.items || [],
        selectedProgram: program,
        isLoading: false,
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to paste task',
        isLoading: false,
      })
      throw error
    }
  },

  // Clear clipboard
  clearClipboard: () => {
    set({ clipboardTask: null })
  },
}))
